import Image from "next/image";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "next-i18next";

const ExplainComponent = () => {
    const { t } = useTranslation();
    const explainVidRef = useRef<HTMLIFrameElement | null>(null);
    const [isVideoIconShown, setIsVideoIconShown] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (explainVidRef.current) {
                        const src = explainVidRef.current.src;
                        if (entry.isIntersecting) {
                            if (!src.includes("autoplay=1")) {
                                explainVidRef.current.src = `${src}?autoplay=1`;
                            }
                            setIsVideoIconShown(true);
                        } else {
                            const pausedSrc = src.replace("?autoplay=1", "");
                            explainVidRef.current.src = pausedSrc;
                            setIsVideoIconShown(false);
                        }
                    }
                });
            },
            {
                threshold: 0.5, // Adjust this value as needed
            }
        );

        if (explainVidRef.current) {
            observer.observe(explainVidRef.current);
        }

        return () => {
            if (explainVidRef.current) {
                observer.unobserve(explainVidRef.current);
            }
        };
    }, []);

    return (
        <div className="w-full text-primary child:flex child:items-center child:justify-center py-[5rem] px-[1rem] md:px-[15rem] overflow-hidden ">
            <div className="text-[2rem] font-bold">
                {t("home.explain.title")}
            </div>
            <div className="text-center text-[#636363] !items-start">
                <p className="text-center w-[80%] mt-[1rem] text-[1.5rem]">
                    {t("home.explain.subtitle")}
                </p>
            </div>

            <div className="h-[32rem] text-[1rem]">
                <div className="bg-primary text-[white] h-auto md:h-[25rem] mt-[5rem] p-[4rem] rounded-[3rem] relative">
                    <Image
                        data-aos="fade-left"
                        src={"/explain/loudIco.svg"}
                        width={1000}
                        height={1000}
                        alt="explain"
                        className="w-[10rem]"
                    />
                    <div className="text-[1.1rem] md:text-[1.5rem] w-[42%] md:w-[40%] mt-[1rem]">
                        {t("home.explain.videoDescription")}
                    </div>
                    <div className="[&_.ytp-large-play-button-red-bg]:!hidden text-[1.2rem] md:text-[1rem] relative">
                        <iframe
                            ref={explainVidRef}
                            className="absolute -top-[18.0rem] left-[2rem] md:left-[6rem] w-[12rem] md:w-[15rem] h-[20rem] md:h-[27rem] rounded-[1rem] z-[15]"
                            src="https://www.youtube.com/embed/7f8mL7esPe4"
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        ></iframe>
                        <Image
                            data-aos="zoom-out"
                            data-aos-duration="600"
                            onClick={() => {
                                if (explainVidRef.current) {
                                    const src = explainVidRef.current.src;
                                    if (!src.includes("autoplay=1")) {
                                        explainVidRef.current.src = `${src}?autoplay=1`;
                                    }
                                    setIsVideoIconShown(true);
                                }
                            }}
                            src={"/icons/videoIco.svg"}
                            width={1000}
                            height={1000}
                            alt="explain"
                            className={`w-[12rem] md:w-[8rem] absolute -top-[10.5rem] md:-top-[8.5rem] left-[7.5rem] md:left-[9.5rem] cursor-pointer z-[20] ${
                                isVideoIconShown && "hidden"
                            }`}
                        />
                    </div>
                    <Image
                        data-aos="fade-right"
                        src={"/explain/mobile-trans.svg"}
                        width={1000}
                        height={1000}
                        alt="explain"
                        className="w-[10rem] md:w-[12rem] absolute -top-[0rem] left-[2rem] md:left-[7rem]"
                    />
                    <Image
                        data-aos="zoom-in"
                        data-aos-duration="700"
                        src={"/explain/mobile.svg"}
                        width={1000}
                        height={1000}
                        alt="explain"
                        className="w-[14rem] absolute -top-[2rem] left-[15rem] hidden"
                    />

                    <Image
                        src={"/icons/dots.svg"}
                        width={1000}
                        height={1000}
                        alt="explain"
                        className="w-[6rem] absolute -bottom-[2rem] left-[2.3rem]"
                    />
                </div>
            </div>
        </div>
    );
};

export default ExplainComponent;
